import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { ChevronDown, ChevronRight, Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { profileSectionManageButtonClass } from '@/lib/profile-section-styles'
import { cn } from '@/lib/utils'

export type EntityManageSectionProps = {
  title: string
  subtitle?: string
  leadingIcon?: LucideIcon
  onEdit?: () => void
  editAriaLabel?: string
  editTitle?: string
  highlighted?: boolean
  agentHighlight?: string
  className?: string
  /** When true, body collapses; header remains a disclosure trigger. */
  collapsible?: boolean
  defaultOpen?: boolean
  children: ReactNode
}

/** Compact icon + title + subtitle section card with optional header edit pencil (job skills, job description read, etc.). */
export function EntityManageSection({
  title,
  subtitle,
  leadingIcon: LeadingIcon,
  onEdit,
  editAriaLabel,
  editTitle,
  highlighted = false,
  agentHighlight,
  className,
  collapsible = false,
  defaultOpen = true,
  children,
}: EntityManageSectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  const titleBlock = (
    <div>
      <CardTitle className="flex items-center gap-2 text-base">
        {LeadingIcon ? <LeadingIcon className="h-5 w-5" aria-hidden /> : null}
        {title}
        {collapsible ? (
          open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
          )
        ) : null}
      </CardTitle>
      {subtitle ? <CardDescription alwaysShow>{subtitle}</CardDescription> : null}
    </div>
  )

  const editAction = onEdit ? (
    <CardAction>
      <Button
        type="button"
        variant="ghostToolbar"
        size="iconSm"
        className={profileSectionManageButtonClass}
        aria-label={editAriaLabel}
        title={editTitle}
        onClick={(e) => {
          e.stopPropagation()
          onEdit()
        }}
      >
        <Pencil className="h-4 w-4" />
      </Button>
    </CardAction>
  ) : null

  const cardClassName = cn(
    'group/section relative z-0 transition-all duration-300',
    highlighted ? 'bg-success/5 ring-2 ring-success/50' : undefined,
    className,
  )
  const agentProps = agentHighlight ? { 'data-agent-highlight': agentHighlight } : {}

  if (!collapsible) {
    return (
      <Card className={cardClassName} {...agentProps}>
        <CardHeader>
          <div className="flex w-full items-start justify-between gap-2">
            {titleBlock}
            {editAction}
          </div>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    )
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className={cardClassName} {...agentProps}>
        <CardHeader>
          <div className="flex w-full items-start justify-between gap-2">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
              >
                {titleBlock}
              </button>
            </CollapsibleTrigger>
            {editAction}
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
